<template>
  <div class="w-full">
    <!-- Card 1: Identitas AI -->
    <b-card class="border border-gray-200 mb-3">
      <div class="mb-2 pb-1 border-b">
        <h5 class="text-base font-semibold text-[#333333] mb-0.5">
          Identitas AI
        </h5>
        <p class="text-xs text-[#828282] mb-0">
          Informasi dasar tentang AI Customer Service kamu
        </p>
      </div>

      <b-row>
        <!-- Nama Toko/Brand -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Nama Toko/Brand<span class="text-danger">*</span>
            </template>
            <b-form-input
              :value="formData.brand"
              type="text"
              placeholder="Masukkan nama toko/brand"
              maxlength="40"
              @input="$emit('update:brand', $event)"
            />
            <div class="text-right text-xs text-muted mt-0.5">
              <span>{{ (formData.brand || '').length }}/40</span>
            </div>
          </b-form-group>
        </b-col>

        <!-- Nama Customer Service AI -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Nama Customer Service AI<span class="text-danger">*</span>
            </template>
            <b-form-input
              :value="formData.name"
              type="text"
              placeholder="Masukkan nama customer service AI"
              maxlength="40"
              @input="$emit('update:name', $event)"
            />
            <div class="text-right text-xs text-muted mt-0.5">
              <span>{{ (formData.name || '').length }}/40</span>
            </div>
          </b-form-group>
        </b-col>

        <!-- Pilih Nomor Untuk CS AI -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Pilih Nomor Untuk CS AI <span class="text-danger">*</span>
            </template>
            <PhoneNumberSelect
              :value="formData.phone_number"
              @input="$emit('update:phone_number', $event)"
            />
          </b-form-group>
        </b-col>

        <!-- Jenis Kelamin CS AI -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Jenis Kelamin CS AI <span class="text-danger">*</span>
            </template>
            <b-form-radio-group
              :checked="normalizedGender"
              :options="genderOptions"
              name="gender-radio"
              class="d-flex align-items-center gap-4 pt-1 text-sm"
              @change="onSelectGender"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Card 2: Personality AI -->
    <b-card class="border border-gray-200 mb-3">
      <div class="mb-2 pb-1 border-b">
        <h5 class="text-base font-semibold text-[#333333] mb-0.5">
          Personality AI
        </h5>
        <p class="text-xs text-[#828282] mb-0">
          Tentukan karakter dan cara AI berkomunikasi
        </p>
      </div>

      <b-row>
        <!-- Gaya Bahasa -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Gaya Bahasa<span class="text-danger">*</span>
            </template>
            <b-form-input
              :value="formData.language_style"
              type="text"
              placeholder="Pilih gaya bahasa"
              maxlength="40"
              @input="$emit('update:language_style', $event)"
            />
            <div class="text-right text-xs text-muted mt-0.5">
              <span>{{ (formData.language_style || '').length }}/40</span>
            </div>
          </b-form-group>
        </b-col>

        <!-- Sapaan CS AI -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Sapaan CS AI<span class="text-danger">*</span>
            </template>
            <b-form-input
              :value="formData.greeting"
              type="text"
              placeholder="Pilih sapaan CS AI"
              maxlength="40"
              @input="$emit('update:greeting', $event)"
            />
            <div class="text-right text-xs text-muted mt-0.5">
              <span>{{ (formData.greeting || '').length }}/40</span>
            </div>
          </b-form-group>
        </b-col>

        <!-- Deskripsi Pekerjaan (Full width) -->
        <b-col cols="12">
          <b-form-group
            id="input-group-jobdesc"
            label-for="textarea-jobdesc"
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              <div class="flex justify-between items-center">
                <div>
                  Deskripsi Pekerjaan<span class="text-danger">*</span>
                </div>
                <div class="flex gap-2">
                  <b-button
                    id="popover-target-1"
                    variant="outline"
                    size="sm"
                    class="!w-fit !p-1 !border-[#E2E2E2]"
                    @click="onResetJobDesc"
                  >
                    <b-icon-arrow-clockwise class="text-dark" />
                  </b-button>
                  <b-popover
                    target="popover-target-1"
                    triggers="hover"
                    placement="top"
                    custom-class="custom-popover"
                  >
                    <div>
                      Reset
                    </div>
                  </b-popover>
                  <b-button
                    :variant="isOnEdit ? 'primary' : 'outline'"
                    size="sm"
                    class="!p-1 !border-[#E2E2E2]"
                    @click="onEditJobDesc"
                  >
                    {{ isOnEdit ? 'Simpan' : 'Edit' }}
                  </b-button>
                </div>
              </div>
            </template>
            <b-form-textarea
              id="textarea-jobdesc"
              :value="formData.job_description"
              :disabled="!isOnEdit"
              class="min-h-[108px] resize-y"
              maxlength="2500"
              placeholder="Masukkan deskripsi pekerjaan Customer Service kamu"
              @input="$emit('update:job_description', $event)"
            />
            <div class="text-right text-xs text-muted mt-0.5">
              <span>{{ (formData.job_description || '').length }}/2500 Karakter</span>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import { jobdescDefault } from './config'
import PhoneNumberSelect from './PhoneNumberSelect.vue'

export default {
  name: 'StepOne',
  components: {
    PhoneNumberSelect,
  },
  props: {
    formData: {
      type: Object,
      default: () => ({}),
    },
    disableNext: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOnEdit: false,
      defaultJobDesc: jobdescDefault,
      genderOptions: [
        { text: 'Laki-Laki', value: 'male' },
        { text: 'Perempuan', value: 'female' },
      ],
    }
  },
  computed: {
    normalizedGender() {
      const g = String(this.formData.gender || '').toLowerCase().trim()
      if (['male', 'laki-laki', 'pria', 'm', '1'].includes(g)) {
        return 'male'
      }
      if (['female', 'perempuan', 'wanita', 'f', '2'].includes(g)) {
        return 'female'
      }
      return g
    },
  },
  watch: {
    formData: {
      handler() {
        this.validateForm()
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.validateForm()
  },
  methods: {
    onSelectGender(val) {
      this.$emit('update:gender', val)
    },
    onEditJobDesc() {
      this.isOnEdit = !this.isOnEdit
      if (!this.isOnEdit) {
        this.validateForm()
      }
    },
    onResetJobDesc() {
      this.$emit('update:job_description', this.defaultJobDesc)
    },
    validateForm() {
      const requiredFields = [
        'brand',
        'name',
        'phone_number',
        'gender',
        'language_style',
        'greeting',
        'job_description',
      ]

      const isFormValid = requiredFields.every(field => {
        const value = this.formData[field]
        if (field === 'phone_number') {
          const raw = typeof value === 'object' && value ? value.phone_number : value
          return raw !== undefined && raw !== null && String(raw).trim() !== ''
        }
        return value !== undefined && value !== null && String(value).trim() !== ''
      })

      this.$emit('update:disable-next', !isFormValid)
    },
  },
}
</script>
